import { useState, useEffect, useCallback, useRef } from "react";
import { Pressable, Modal } from "react-native";

import { FormInput } from "@src/components/FormInput";
import { View, Text } from "@src/components";
import { Button } from "@src/components/Button";
import {
  usePickerField,
  formatTimeWith24Hour,
  parseTimeStringWith24Hour,
  isLastMinute,
  MIDNIGHT_24,
  PICKER_CONSTANTS,
  type TimeValue,
} from "../Pickers";
import { useUnistyles } from "react-native-unistyles";

type Hour = number; // 0–24
type Minute = number; // 0–59

interface TimePickerWith24HourProps {
  isVisible: boolean;
  hour: Hour;
  minute: Minute;
  onConfirm: (hour: Hour, minute: Minute) => void;
  onCancel: () => void;
  initialHour24?: boolean;
}

const MODAL_DELAY_MS = 100;

const CONFIRM_MESSAGES = {
  QUESTION: "Did you mean midnight (end of the day)?",
} as const;

const resolveTimeValue = (hour: Hour, minute: Minute, initialHour24: boolean): TimeValue => {
  if (initialHour24 && hour === 0 && minute === 0) {
    return MIDNIGHT_24;
  }
  return { hour, minute };
};

export function TimePickerWith24Hour({
  isVisible,
  hour,
  minute,
  onConfirm,
  onCancel,
  initialHour24 = false,
}: TimePickerWith24HourProps) {
  usePickerField();
  const { theme } = useUnistyles();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [webTimeInput, setWebTimeInput] = useState<string>("");
  const [webTimeError, setWebTimeError] = useState<string>("");

  useEffect(() => {
    if (isVisible) {
      setWebTimeInput(formatTimeWith24Hour(hour, minute));
      setWebTimeError("");
    }
  }, [isVisible, hour, minute]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const handleWebTimeInputChange = useCallback((text: string) => {
    setWebTimeInput(text);
    setWebTimeError("");
  }, []);

  const showMidnightConfirmation = useCallback((onYes: () => void, onNo: () => void) => {
    if (window.confirm(CONFIRM_MESSAGES.QUESTION)) {
      onYes();
    } else {
      onNo();
    }
  }, []);

  const handleTimeConfirm = useCallback(
    (selectedHour: Hour, selectedMinute: Minute) => {
      if (isLastMinute(selectedHour, selectedMinute)) {
        onCancel();
        timeoutRef.current = setTimeout(() => {
          showMidnightConfirmation(
            () => onConfirm(MIDNIGHT_24.hour, MIDNIGHT_24.minute),
            () => onConfirm(selectedHour, selectedMinute),
          );
        }, MODAL_DELAY_MS);
      } else {
        const resolved = resolveTimeValue(selectedHour, selectedMinute, initialHour24);
        onConfirm(resolved.hour, resolved.minute);
      }
    },
    [onConfirm, onCancel, initialHour24, showMidnightConfirmation],
  );

  const handleWebTimeConfirm = useCallback(() => {
    const parsed = parseTimeStringWith24Hour(webTimeInput);
    if (!parsed.success || !parsed.value) {
      setWebTimeError(parsed.error ?? "Invalid time");
      return;
    }

    handleTimeConfirm(parsed.value.hour, parsed.value.minute);
    onCancel();
  }, [webTimeInput, handleTimeConfirm, onCancel]);

  if (!isVisible) {
    return null;
  }

  return (
    <Modal visible={isVisible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable
        style={{
          flex: 1,
          backgroundColor: `rgba(0, 0, 0, ${PICKER_CONSTANTS.MODAL_OPACITY})`,
          justifyContent: "center",
          alignItems: "center",
        }}
        onPress={onCancel}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={{
            backgroundColor: theme.colors.background,
            borderRadius: 12,
            padding: 24,
            width: PICKER_CONSTANTS.MODAL_WIDTH_PERCENT,
            maxWidth: PICKER_CONSTANTS.MODAL_MAX_WIDTH,
          }}
        >
          <Text textStyle="lg/semibold" mb="xl">
            Enter Time
          </Text>
          <Text color="textMuted" textStyle="sm/regular" mb="md">
            Format: HH:MM (24-hour, e.g., 14:30 or 24:00)
          </Text>
          <FormInput
            value={webTimeInput}
            onChangeText={handleWebTimeInputChange}
            placeholder="14:30"
            keyboardType="numeric"
            error={webTimeError}
            onSubmitEditing={handleWebTimeConfirm}
          />
          <View flexDirection="row" gap="md" mt="xl" justifyContent="flex-end">
            <Button variant="secondary" onPress={onCancel} label="Cancel" />
            <Button variant="primary" onPress={handleWebTimeConfirm} label="Confirm" />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
